/**
 * TeamChatView — Restricted landing page for `chat_member` role.
 * M1: shows chat context + waiting-for-content shell. Feed arrives in M2.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, LogOut, Loader2, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamChatView() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState(null);
  const [availableChats, setAvailableChats] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [chatRes, listRes] = await Promise.all([
        axios.get(`${API}/team-chat/${chatId}`, { headers }),
        axios.get(`${API}/team-chat`, { headers }),
      ]);
      setChat(chatRes.data);
      setAvailableChats(listRes.data.member_chats || []);
    } catch (err) {
      toast.error('Could not load this chat');
      // fall back to first available chat
      try {
        const token = localStorage.getItem('token');
        const listRes = await axios.get(`${API}/team-chat`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const chats = listRes.data.member_chats || [];
        setAvailableChats(chats);
        if (chats.length > 0 && chats[0].id !== chatId) {
          navigate(`/team-chat/${chats[0].id}`, { replace: true });
          return;
        }
      } catch {
        // Ignore secondary lookup failure — user will see the empty state.
      }
    } finally {
      setLoading(false);
    }
  }, [chatId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-[#007a33] animate-spin" />
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-6 text-center">
          <p className="text-sm text-gray-600 mb-4">You aren&apos;t part of any chats yet.</p>
          <Button onClick={logout} variant="outline"><LogOut className="w-4 h-4 mr-1.5" />Log out</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#007a33] text-white px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="font-black truncate">{chat.title}</p>
          <p className="text-xs text-emerald-100 truncate">{chat.season_name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {availableChats.length > 1 && (
            <select
              value={chatId}
              onChange={(e) => navigate(`/team-chat/${e.target.value}`)}
              className="text-xs bg-emerald-900/40 border border-emerald-700 rounded px-2 py-1"
              data-testid="chat-switcher"
            >
              {availableChats.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          )}
          <Button size="sm" variant="ghost" onClick={logout} className="text-white hover:bg-emerald-800" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="team-chat-view-page">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">Waiting for your coach&apos;s first post</h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Announcements, match &amp; training availability polls and messages from your coach will appear here.
            You&apos;ll be notified by email whenever something new is posted.
          </p>
        </div>

        <div className="mt-6 p-3 rounded-lg bg-gray-100 border border-gray-200 flex items-center gap-2 text-xs text-gray-600">
          <Users className="w-4 h-4" />
          <span>Coach: <strong>{chat.coach_name || 'Your coach'}</strong>. Direct messages to the coach coming soon.</span>
        </div>
      </main>
    </div>
  );
}
